import React from 'react';

export interface CloseIconProps extends React.SVGProps<SVGSVGElement> {
  /** Width/height in px (defaults to 16). */
  size?: number | string;
  /** Optional accessible name; when provided the icon is exposed to assistive tech. */
  title?: string;
}

/**
 * Close cross used on modal headers. Strokes use `currentColor` so the icon takes
 * the colour of its button. Decorative by default (aria-hidden) since the button
 * itself carries the accessible label.
 * @param props - component props (any valid SVG attribute may also be passed).
 * @returns the close icon SVG element.
 */
function CloseIcon({ size = 16, className = '', title, ...rest }: CloseIconProps) {
  const accessibilityProps = title
    ? { role: 'img', 'aria-label': title }
    : { 'aria-hidden': true, focusable: false };

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      {...accessibilityProps}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Pure presentational icon — memoised so it never re-renders unless its props change. */
export default React.memo(CloseIcon);
